/**
 * SiteOverridesProvider - applies live overrides fetched from /api/site-overrides:
 *  - CSS custom properties (--primary, --accent, ...) via inline style
 *  - Text replacements via DOM TreeWalker + MutationObserver
 *  - Hide elements via injected CSS
 *  - Specific text keys (hero_title, phone, ...) exposed via context to components
 *
 * Lightweight, safe: only reads; changes are reversible by backend.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SiteOverridesContext = createContext({ texts: {}, colors: {}, refresh: () => {} });

export const useSiteOverrides = () => useContext(SiteOverridesContext);
export const useSiteText = (key, fallback = '') => {
  const { texts } = useSiteOverrides();
  return texts[key] ?? fallback;
};

// Color key → CSS property name
const COLOR_VAR = {
  primary: '--site-primary',
  accent: '--site-accent',
  background: '--site-background',
  text: '--site-text',
  button: '--site-button',
  button_text: '--site-button-text',
  banner_bg: '--site-banner-bg',
  banner_text: '--site-banner-text',
  link: '--site-link',
  header_bg: '--site-header-bg',
  footer_bg: '--site-footer-bg',
};

// --- Global mutable map of current text replacements (so MutationObserver sees latest) ---
let _currentReplaces = [];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'INPUT']);

function walkAndReplace(root, replaces) {
  if (!root || replaces.length === 0) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentNode;
      if (!p || SKIP_TAGS.has(p.nodeName)) return NodeFilter.FILTER_REJECT;
      if (p.closest && p.closest('[data-no-override]')) return NodeFilter.FILTER_REJECT;
      return node.nodeValue && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  let node;
  while ((node = walker.nextNode())) {
    let txt = node.nodeValue;
    let changed = false;
    for (const { from, to } of replaces) {
      if (!from) continue;
      if (txt.includes(from)) {
        txt = txt.split(from).join(to);
        changed = true;
      }
    }
    if (changed) node.nodeValue = txt;
  }
}

function applyColors(colors) {
  const root = document.documentElement;
  // Clear previous inline color properties first
  Object.values(COLOR_VAR).forEach(v => root.style.removeProperty(v));
  for (const [k, v] of Object.entries(colors || {})) {
    const cssVar = COLOR_VAR[k];
    if (cssVar && v) root.style.setProperty(cssVar, v);
  }

  // Also publish a style tag that maps our variables to common Tailwind utility selectors
  // so user changes take visible effect without touching the source components.
  let styleEl = document.getElementById('site-overrides-css');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'site-overrides-css';
    document.head.appendChild(styleEl);
  }
  const c = colors || {};
  const rules = [];
  if (c.primary) rules.push(`.bg-\\[\\#2C3E2D\\], .bg-emerald-700, .bg-emerald-800, .bg-emerald-900 { background-color: ${c.primary} !important; }`);
  if (c.primary) rules.push(`.text-\\[\\#2C3E2D\\] { color: ${c.primary} !important; }`);
  if (c.accent) rules.push(`.bg-\\[\\#D97706\\], .bg-orange-500, .bg-orange-600 { background-color: ${c.accent} !important; }`);
  if (c.accent) rules.push(`.text-\\[\\#D97706\\] { color: ${c.accent} !important; }`);
  if (c.button) rules.push(`button.bg-emerald-600, button.bg-orange-500, .site-primary-btn { background-color: ${c.button} !important; }`);
  if (c.button_text) rules.push(`button.bg-emerald-600, button.bg-orange-500, .site-primary-btn { color: ${c.button_text} !important; }`);
  if (c.banner_bg) rules.push(`.top-banner, [class*="top-banner"] { background-color: ${c.banner_bg} !important; }`);
  if (c.banner_text) rules.push(`.top-banner, [class*="top-banner"], .top-banner * { color: ${c.banner_text} !important; }`);
  if (c.background) rules.push(`body { background-color: ${c.background} !important; }`);
  if (c.link) rules.push(`a { color: ${c.link} !important; }`);
  if (c.footer_bg) rules.push(`footer, [class*="footer"] { background-color: ${c.footer_bg} !important; }`);
  if (c.header_bg) rules.push(`header { background-color: ${c.header_bg} !important; }`);
  styleEl.textContent = rules.join('\n');
}

function applyHides(selectors) {
  let styleEl = document.getElementById('site-overrides-hide');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'site-overrides-hide';
    document.head.appendChild(styleEl);
  }
  if (!selectors || selectors.length === 0) {
    styleEl.textContent = '';
    return;
  }
  styleEl.textContent = selectors.filter(Boolean).map(s => `${s} { display: none !important; }`).join('\n');
}

function applyImages(images) {
  // Apply banner/hero/logo image overrides via injected CSS
  let styleEl = document.getElementById('site-overrides-images');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'site-overrides-images';
    document.head.appendChild(styleEl);
  }
  if (!images || Object.keys(images).length === 0) {
    styleEl.textContent = '';
    return;
  }
  const rules = [];
  const absUrl = (u) => u && u.startsWith('/') ? (process.env.REACT_APP_BACKEND_URL + u) : u;
  if (images.banner) {
    const u = absUrl(images.banner);
    rules.push(`.top-banner, [class*="top-banner"], .banner-image, [data-site-image="banner"] { background-image: url("${u}") !important; background-size: cover !important; background-position: center !important; }`);
  }
  if (images.hero) {
    const u = absUrl(images.hero);
    rules.push(`.hero-image, [data-site-image="hero"] { background-image: url("${u}") !important; background-size: cover !important; background-position: center !important; }`);
    rules.push(`img[data-site-image="hero"], .hero-image img { content: url("${u}") !important; }`);
  }
  if (images.logo) {
    const u = absUrl(images.logo);
    rules.push(`img[alt*="logo" i], img[data-site-image="logo"], .site-logo img { content: url("${u}") !important; }`);
  }
  styleEl.textContent = rules.join('\n');
}

function applyCustomCss(cssRules) {
  // cssRules: [{scope:'global|store|dashboard', css, id}]
  let styleEl = document.getElementById('site-overrides-custom-css');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'site-overrides-custom-css';
    document.head.appendChild(styleEl);
  }
  if (!cssRules || cssRules.length === 0) {
    styleEl.textContent = '';
    return;
  }
  const path = window.location.pathname;
  const isDashboard = path.startsWith('/bari') || path.startsWith('/admin');
  const isStore = !isDashboard;
  const combined = cssRules.filter(r => {
    if (r.scope === 'global') return true;
    if (r.scope === 'store' && isStore) return true;
    if (r.scope === 'dashboard' && isDashboard) return true;
    return false;
  }).map(r => `/* ${r.name || r.id || ''} */\n${r.css}`).join('\n\n');
  styleEl.textContent = combined;
}

function applyFavicon(url) {
  if (!url) return;
  const abs = url.startsWith('/') ? (process.env.REACT_APP_BACKEND_URL + url) : url;
  let link = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = abs;
}

function applyPageTitle(title) {
  if (!title) return;
  document.title = title;
}

function applyMetas(metas) {
  if (!metas) return;
  for (const [name, content] of Object.entries(metas)) {
    const isOg = name.startsWith('og:');
    const attr = isOg ? 'property' : 'name';
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }
}

function applyImageSelectors(imageSelectors) {
  // Apply arbitrary CSS-selector-based image overrides
  let styleEl = document.getElementById('site-overrides-image-selectors');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'site-overrides-image-selectors';
    document.head.appendChild(styleEl);
  }
  if (!imageSelectors || imageSelectors.length === 0) {
    styleEl.textContent = '';
    return;
  }
  const absUrl = (u) => u && u.startsWith('/') ? (process.env.REACT_APP_BACKEND_URL + u) : u;
  const rules = imageSelectors.map(({ selector, url }) => {
    if (!selector || !url) return '';
    const u = absUrl(url);
    // Apply as background-image for divs and as content for <img>
    return `${selector} { background-image: url("${u}") !important; background-size: cover !important; background-position: center !important; }
${selector}:is(img), ${selector} img { content: url("${u}") !important; }`;
  }).join('\n');
  styleEl.textContent = rules;
}

export default function SiteOverridesProvider({ children }) {
  const [texts, setTexts] = useState({});
  const [colors, setColors] = useState({});

  const fetchOverrides = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/site-overrides`);
      const d = r.data || {};
      _currentReplaces = (d.text_replaces || []).filter(x => x.from);
      setTexts(d.texts || {});
      setColors(d.colors || {});
      applyColors(d.colors || {});
      applyHides(d.hides || []);
      applyImages(d.images || {});
      applyImageSelectors(d.image_selectors || []);
      applyCustomCss(d.css_rules || []);
      applyFavicon(d.favicon);
      applyPageTitle(d.page_title);
      applyMetas(d.metas || {});
      // Apply text replacements to whole document
      walkAndReplace(document.body, _currentReplaces);
    } catch (e) {
      // ignore - site still works
    }
  }, []);

  useEffect(() => {
    fetchOverrides();

    // Listen to explicit refresh events from admin chat
    const onRefresh = () => fetchOverrides();
    window.addEventListener('site-overrides-refresh', onRefresh);

    // MutationObserver to re-apply text replaces on DOM updates (React re-renders)
    const observer = new MutationObserver(muts => {
      if (_currentReplaces.length === 0) return;
      for (const m of muts) {
        m.addedNodes.forEach(n => {
          if (n.nodeType === 1) walkAndReplace(n, _currentReplaces);
          else if (n.nodeType === 3 && n.parentNode && !SKIP_TAGS.has(n.parentNode.nodeName)) {
            // characterData change will handle via separate observer channel below
            let txt = n.nodeValue, changed = false;
            for (const { from, to } of _currentReplaces) {
              if (from && txt.includes(from)) { txt = txt.split(from).join(to); changed = true; }
            }
            if (changed) n.nodeValue = txt;
          }
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    // Poll for new overrides every 30s (cheap) so admin sees live updates quickly
    const poll = setInterval(fetchOverrides, 30000);

    return () => { observer.disconnect(); clearInterval(poll); window.removeEventListener('site-overrides-refresh', onRefresh); };
  }, [fetchOverrides]);

  return (
    <SiteOverridesContext.Provider value={{ texts, colors, refresh: fetchOverrides }}>
      {children}
    </SiteOverridesContext.Provider>
  );
}
